import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  ChevronDown,
  Database,
  HardDriveDownload,
  LayoutDashboard,
  Package,
  RefreshCcw,
  Settings2,
  Sparkles,
  Wrench,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Skeleton } from "@/components/ui/skeleton";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { useChangelog, useMarkReleaseNotesSeen } from "../../hooks/useDocs";
import {
  ChangelogCategory,
  ChangelogCategoryOrder,
  ChangelogCategoryTitles,
  ChangelogComponent,
  ChangelogComponentLabels,
  ChangelogEntry,
  ChangelogImpact,
  ChangelogImpactDescriptions,
  ChangelogImpactLabels,
  ChangelogRelease,
  compareReleaseIds,
  releaseAnchor,
} from "../../models/Docs";
import MarkdownContent from "./MarkdownContent";

const CATEGORY_STYLE: Record<ChangelogCategory, { icon: React.ElementType; className: string }> = {
  install: { icon: HardDriveDownload, className: "text-amber-700 dark:text-amber-300" },
  "db-sync": { icon: Database, className: "text-sky-700 dark:text-sky-300" },
  updater: { icon: RefreshCcw, className: "text-violet-700 dark:text-violet-300" },
  config: { icon: Settings2, className: "text-teal-700 dark:text-teal-300" },
  portal: { icon: LayoutDashboard, className: "text-primary" },
  fixes: { icon: Wrench, className: "text-emerald-700 dark:text-emerald-300" },
};

const IMPACT_VARIANT: Record<ChangelogImpact, "danger" | "warning" | "neutral"> = {
  "action-required": "danger",
  "heads-up": "warning",
  "no-action": "neutral",
};

type ComponentFilter = "all" | ChangelogComponent;
type ImpactFilter = "all" | "action-required";

export const formatReleaseDate = (isoDate: string) =>
  new Date(`${isoDate}T00:00:00`).toLocaleDateString(undefined, { day: "numeric", month: "long", year: "numeric" });

const matches = (entry: ChangelogEntry, component: ComponentFilter, impact: ImpactFilter) =>
  (component === "all" || entry.components.includes(component)) && (impact === "all" || entry.impact === impact);

const EntryView: React.FC<{ entry: ChangelogEntry; baseSlug: string }> = ({ entry, baseSlug }) => (
  <div id={entry.anchor} className="scroll-mt-4 border-t py-4 first:border-t-0 first:pt-2">
    <div className="flex flex-wrap items-center gap-2">
      <h4 className="text-base font-semibold">{entry.title}</h4>
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge variant={IMPACT_VARIANT[entry.impact]}>{ChangelogImpactLabels[entry.impact]}</Badge>
        </TooltipTrigger>
        <TooltipContent>{ChangelogImpactDescriptions[entry.impact]}</TooltipContent>
      </Tooltip>
      {entry.components.map((component) => (
        <Badge key={component} variant="outline">{ChangelogComponentLabels[component]}</Badge>
      ))}
    </div>
    <MarkdownContent markdown={entry.markdown} baseSlug={baseSlug} className="mt-2" />
  </div>
);

export const EntriesByCategory: React.FC<{ entries: ChangelogEntry[]; baseSlug: string }> = ({ entries, baseSlug }) => (
  <>
    {ChangelogCategoryOrder.map((category) => {
      const inCategory = entries.filter((e) => e.category === category);
      if (inCategory.length === 0) return null;
      const style = CATEGORY_STYLE[category];
      const Icon = style.icon;
      return (
        <section key={category} className="mt-4">
          <h3 className={cn("flex items-center gap-2 text-sm font-semibold uppercase tracking-wide", style.className)}>
            <Icon className="size-4" />
            {ChangelogCategoryTitles[category]}
          </h3>
          <div className="mt-1">
            {inCategory.map((entry) => <EntryView key={entry.anchor} entry={entry} baseSlug={baseSlug} />)}
          </div>
        </section>
      );
    })}
  </>
);

const BuildChips: React.FC<{ release: ChangelogRelease }> = ({ release }) => (
  <div className="flex flex-wrap gap-1.5">
    {release.server && <Badge variant="neutral"><Package />Server &amp; portal</Badge>}
    {release.client && <Badge variant="neutral"><Package />Client {release.client.version}</Badge>}
    {release.updater && <Badge variant="neutral"><Package />Updater {release.updater.version}</Badge>}
  </div>
);

const ReleaseCard: React.FC<{
  release: ChangelogRelease;
  isNew: boolean;
  component: ComponentFilter;
  impact: ImpactFilter;
}> = ({ release, isNew, component, impact }) => {
  const baseSlug = `changelog/${release.id}`;
  const allEntries = release.sections.flatMap((s) => s.entries);
  const entries = allEntries.filter((e) => matches(e, component, impact));
  const actionRequired = allEntries.filter((e) => e.impact === "action-required").length;
  const filtered = component !== "all" || impact !== "all";

  return (
    <Card id={releaseAnchor(release.id)} className="scroll-mt-4 gap-0 p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="text-xl font-semibold">{formatReleaseDate(release.date)}</h2>
            {isNew && <Badge variant="primary"><Sparkles />New</Badge>}
            {actionRequired > 0 && <Badge variant="danger">{actionRequired} action required</Badge>}
          </div>
          <p className="mt-1 text-sm text-muted-foreground">{release.summary}</p>
        </div>
        <BuildChips release={release} />
      </div>

      {entries.length > 0 ? (
        <EntriesByCategory entries={entries} baseSlug={baseSlug} />
      ) : (
        <p className="mt-4 text-sm text-muted-foreground">
          {filtered ? "Nothing in this release matches the filter." : "No SE-facing changes in this release."}
        </p>
      )}

      {release.alsoShipped && !filtered && (
        <Collapsible className="mt-4 border-t pt-3">
          <CollapsibleTrigger className="group flex items-center gap-1 text-sm font-medium text-muted-foreground hover:text-foreground">
            <ChevronDown className="size-4 transition-transform group-data-[state=open]:rotate-180" />
            Also shipped (no SE-facing change)
          </CollapsibleTrigger>
          <CollapsibleContent>
            <MarkdownContent markdown={release.alsoShipped} baseSlug={baseSlug} className="mt-2" />
          </CollapsibleContent>
        </Collapsible>
      )}

      {(release.serverPullRequests.length > 0 || release.clientPullRequests.length > 0) && (
        <p className="mt-3 text-xs text-muted-foreground">
          {[
            release.serverPullRequests.length > 0 && `Server PRs ${release.serverPullRequests.join(", ")}`,
            release.clientPullRequests.length > 0 && `Client PRs ${release.clientPullRequests.join(", ")}`,
          ].filter(Boolean).join(" · ")}
        </p>
      )}
    </Card>
  );
};

/** The changelog's right-hand outline: jump to the unreleased block or any release. */
export const ChangelogOutline: React.FC = () => {
  const { data } = useChangelog();
  if (!data || (data.releases.length === 0 && data.unreleased.length === 0)) return null;
  return (
    <div className="text-sm">
      <div className="pb-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Releases</div>
      <ul className="space-y-1 border-l">
        {data.unreleased.length > 0 && (
          <li>
            <a href="#unreleased" className="-ml-px block border-l border-transparent py-0.5 pl-3 text-muted-foreground hover:border-foreground hover:text-foreground">
              Not yet released
            </a>
          </li>
        )}
        {data.releases.map((release) => (
          <li key={release.id}>
            <a
              href={`#${releaseAnchor(release.id)}`}
              className="-ml-px block border-l border-transparent py-0.5 pl-3 text-muted-foreground hover:border-foreground hover:text-foreground"
            >
              {formatReleaseDate(release.date)}
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
};

/**
 * Every prod release, newest first, grouped by the categories SEs must not miss. Opening it records the newest
 * release as seen, which clears the What's new badge; the "New" markers use the value from before that write so
 * they stay put while the reader is on the page.
 */
const ChangelogView: React.FC = () => {
  const { data, isLoading, isError } = useChangelog();
  const markSeen = useMarkReleaseNotesSeen();
  const [component, setComponent] = useState<ComponentFilter>("all");
  const [impact, setImpact] = useState<ImpactFilter>("all");
  const seenBeforeVisit = useRef<string | undefined | null>(null);

  if (data && seenBeforeVisit.current === null) seenBeforeVisit.current = data.lastSeenRelease;

  const latest = data?.releases[0]?.id;
  useEffect(() => {
    if (latest && compareReleaseIds(latest, data?.lastSeenRelease) > 0 && !markSeen.isPending && !markSeen.isSuccess)
      markSeen.mutate(latest);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [latest, data?.lastSeenRelease]);

  const unreleased = useMemo(
    () => (data?.unreleased ?? []).filter((e) => matches(e, component, impact)),
    [data, component, impact],
  );

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  if (isError || !data) return <p className="text-sm text-muted-foreground">The changelog could not be loaded.</p>;

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">What's new</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          One entry per production release, covering the server, the portal, the client and the updater. Anything
          marked <span className="font-medium text-destructive">Action required</span> needs you to do something —
          install, set a value, or stop following a procedure.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <ToggleGroup type="single" variant="outline" size="sm" value={component} onValueChange={(v) => v && setComponent(v as ComponentFilter)}>
          <ToggleGroupItem value="all">All components</ToggleGroupItem>
          {(Object.keys(ChangelogComponentLabels) as ChangelogComponent[]).map((c) => (
            <ToggleGroupItem key={c} value={c}>{ChangelogComponentLabels[c]}</ToggleGroupItem>
          ))}
        </ToggleGroup>
        <ToggleGroup type="single" variant="outline" size="sm" value={impact} onValueChange={(v) => v && setImpact(v as ImpactFilter)}>
          <ToggleGroupItem value="all">All changes</ToggleGroupItem>
          <ToggleGroupItem value="action-required">Action required</ToggleGroupItem>
        </ToggleGroup>
      </div>

      {unreleased.length > 0 && (
        <Card id="unreleased" className="gap-0 border-dashed p-5">
          <h2 className="text-xl font-semibold">Not yet released</h2>
          <p className="mt-1 text-sm text-muted-foreground">Merged, and coming in the next release.</p>
          <EntriesByCategory entries={unreleased} baseSlug="changelog/unreleased" />
        </Card>
      )}

      {data.releases.length === 0 && unreleased.length === 0 && (
        <p className="text-sm text-muted-foreground">No releases have been recorded yet.</p>
      )}

      {data.releases.map((release) => (
        <ReleaseCard
          key={release.id}
          release={release}
          isNew={compareReleaseIds(release.id, seenBeforeVisit.current ?? undefined) > 0}
          component={component}
          impact={impact}
        />
      ))}
    </div>
  );
};

export default ChangelogView;
